import * as url from "./url_helper";
import { HttpClient } from "./api_helper";
import {
  ChangePasswordRequest,
  CopyCutSkillRequest,
  CopyCutSubjectRequest,
  CreateBannerRequest,
  CreateCalendarEventRequest,
  CreateChapterRequest,
  CreateCityRequest,
  CreateGovernorateRequest,
  CreateGradeRequest,
  CreateLectureRequest,
  CreateLessonRequest,
  CreatePromoCodeRequest,
  CreateQuestionRequest,
  CreateSchoolRequest,
  CreateSkillRequest,
  CreateSubjectNoteRequest,
  CreateSubjectRequest,
  CreateSupplicationRequest,
  CreateTeacherAdminRequest,
  CreateVerificationRequest,
  ForgetPasswordRequest,
  ManualPurchaseRequest,
  PaginateQuery,
  SendNotificationRequest,
  SendNotificationV3Request,
  SigninRequest,
  SignoutRequest,
  SkillsSuggestionRequest,
  UpdateAppConfigsRequest,
  UpdateBannerRequest,
  UpdateCalendarEventRequest,
  UpdateChapterRequest,
  UpdateCityRequest,
  UpdateCourseDurationRequest,
  UpdateGovernorateRequest,
  UpdateGradeRequest,
  UpdateLectureRequest,
  UpdateLessonRequest,
  UpdatePromoCodeRequest,
  UpdateQuestionRequest,
  UpdateSchoolRequest,
  UpdateSkillRequest,
  UpdateSubjectNoteRequest,
  UpdateSubjectRequest,
  UpdateSupplicationRequest,
  UpdateUserProfileRequest,
  VerificationRequest,
} from "../interfaces";
import { AppSettingsTypeEnum, QuestionModelEnum, RoleEnum } from "../enums";
import {
  CreateFlashCardRequest,
  UpdateFlashCardRequest,
} from "../interfaces/flash-card";
import {
  GenerateSequenceNumberRequest,
  HomeworkSolutionQueryDto,
  UpdateAppSettingsRequest,
} from "../interfaces/app-settings";
import {
  CreateUpdateOlympicRequest,
  CreateOlympicQuestionRequest,
  UpdateOlympicQuestionRequest,
} from "../interfaces/olympic";
import { CreatePlanRequest } from "../interfaces/plan";
import { AdminAnalyticsRequest } from "../interfaces/analytics";
import {
  CreateMediaRequest,
  UploadAndCreateFlashCardRequest,
} from "../interfaces/media";
import { UpdatePlanRequest } from "../interfaces/plan/update-plan-request.interface";
import { SubscriptionReportRequest } from "../interfaces/reports";

const api = new HttpClient();

//#region AUTH
export const signin = (data: SigninRequest) =>
  api.post(url.AUTH_SIGNIN_URL, data);

export const signout = (data: SignoutRequest) =>
  api.post(url.AUTH_SIGNOUT_URL, data);

export const forgetPassword = (data: ForgetPasswordRequest) =>
  api.post(url.AUTH_FORGOT_PASSWORD_URL, data);

export const verifyOtp = (data: VerificationRequest) =>
  api.post(url.AUTH_VERIFY_OTP_URL, data);

export const changeUserPassword = (data: any) =>
  api.patch(url.USER_CHANGE_PASSWORD_URL, data);

export const resetPassword = (data: any) =>
  api.post(`${url.AUTH_RESET_PASSWORD_URL}`, data);

//#endregion

//#region Verification
export const resendOtp = (data: CreateVerificationRequest) =>
  api.post(url.VERIFICATION_RESEND_OTP_URL, data);

//#endregion

//#region Governorates

export const getGovernorates = (data: PaginateQuery) =>
  api.get(url.GET_GOVERNORATES_URL(data));

export const getGovernorate = (id: string) =>
  api.get(url.GET_GOVERNORATE_URL(id));

export const getGovernorateSelectList = () =>
  api.get(url.GET_GOVERNORATE_SELECT_LIST_URL());

export const addGovernorate = (data: CreateGovernorateRequest) =>
  api.post(url.ADD_GOVERNORATE_URL(), data);

export const updateGovernorate = (id: string, data: UpdateGovernorateRequest) =>
  api.patch(url.UPDATE_GOVERNORATE_URL(id), data);

export const deleteGovernorate = (id: string) =>
  api.delete(url.DELETE_GOVERNORATE_URL(id));

//#endregion

//#region Cities

export const getCities = (data: PaginateQuery) =>
  api.get(url.GET_CITIES_URL(data));

export const getCity = (id: string) => api.get(url.GET_CITY_URL(id));

export const getCitySelectList = (governorateId?: string) =>
  api.get(url.GET_CITY_SELECT_LIST_URL(governorateId));

export const addCity = (data: CreateCityRequest) =>
  api.post(url.ADD_CITY_URL(), data);

export const updateCity = (id: string, data: UpdateCityRequest) =>
  api.patch(url.UPDATE_CITY_URL(id), data);

export const deleteCity = (id: string) => api.delete(url.DELETE_CITY_URL(id));

//#endregion

//#region Schools

export const getSchools = (data: PaginateQuery) =>
  api.get(url.GET_SCHOOLS_URL(data));

export const getSchool = (id: string) => api.get(url.GET_SCHOOL_URL(id));

export const getSchoolSelectList = (governorateId: string, cityId: string) =>
  api.get(url.GET_SCHOOL_SELECT_LIST_URL(governorateId, cityId));

export const addSchool = (data: CreateSchoolRequest) =>
  api.post(url.ADD_SCHOOL_URL(), data);

export const updateSchool = (id: string, data: UpdateSchoolRequest) =>
  api.patch(url.UPDATE_SCHOOL_URL(id), data);

export const deleteSchool = (id: string) =>
  api.delete(url.DELETE_SCHOOL_URL(id));

//#endregion

//#region Course

export const getCourseSelectList = () =>
  api.get(url.GET_COURSE_SELECT_LIST_URL());

export const getCourseAndGradesSelectList = () =>
  api.get(url.GET_COURSE_AND_GRADES_SELECT_LIST_URL());

//#endregion

//#region Grade

export const getGrades = (data: PaginateQuery) =>
  api.get(url.GET_GRADES_URL(data));

export const getGrade = (id: string) => api.get(url.GET_GRADE_URL(id));

export const getGradeSelectListByCourse = (courseId: string) =>
  api.get(url.GET_GRADE_SELECT_LIST_BY_COURSE_URL(courseId));

export const getGradesDetailByCourse = (courseId: string) =>
  api.get(url.GET_GRADES_DETAIL_BY_COURSE_URL(courseId));

export const addGrade = (data: CreateGradeRequest) =>
  api.post(url.ADD_GRADE_URL(), data);

export const updateGrade = (id: string, data: UpdateGradeRequest) =>
  api.patch(url.UPDATE_GRADE_URL(id), data);

export const deleteGrade = (id: string) => api.delete(url.DELETE_GRADE_URL(id));

//#endregion

//#region Subject

export const getSubjects = (data: PaginateQuery) =>
  api.get(url.GET_SUBJECTS_URL(data));

export const getSubject = (id: string) => api.get(url.GET_SUBJECT_URL(id));

export const getSubjectsByGrade = (gradeId: string) =>
  api.get(url.GET_SUBJECTS_BY_GRADE_URL(gradeId));

export const addSubject = (data: CreateSubjectRequest) =>
  api.post(url.ADD_SUBJECT_URL(), data);

export const updateSubject = (id: string, data: UpdateSubjectRequest) =>
  api.patch(url.UPDATE_SUBJECT_URL(id), data);

export const deleteSubject = (id: string) =>
  api.delete(url.DELETE_SUBJECT_URL(id));

export const uploadSubjectHomeImage = (file: File, subjectId?: string) =>
  api.uploadFile(url.UPLOAD_SUBJECT_HOME_IMAGE_URL(subjectId), file);

export const uploadSubjectContentImage = (file: File, subjectId?: string) =>
  api.uploadFile(url.UPLOAD_SUBJECT_CONTENT_IMAGE_URL(subjectId), file);

export const copyCutSubject = (data: CopyCutSubjectRequest) =>
  api.post(url.COPY_CUT_SUBJECT(), data);

export const toggleSubjectState = (id: string) =>
  api.patch(url.TOGGLE_SUBJECT_STATE_URL(id), {});

export const getSubjectsSelectListByGrade = (gradeId: string) =>
  api.get(url.GET_SUBJECT_SELECT_LIST_BY_GRADE(gradeId));

export const getPurchaseableSubjectForStudent = (
  courseId: string,
  gradeId: string,
  userId: string
) =>
  api.get(url.GET_PURCHASEABLE_SUBJECTS_FOR_STUDENT(courseId, gradeId, userId));

//#endregion

//#region Subject Note

export const getSubjectNotes = (data: PaginateQuery) =>
  api.get(url.GET_SUBJECT_NOTES_URL(data));

export const getSubjectNote = (id: string) =>
  api.get(url.GET_SUBJECT_NOTE_URL(id));

export const addSubjectNote = (data: CreateSubjectNoteRequest) =>
  api.post(url.ADD_SUBJECT_NOTE_URL(), data);

export const updateSubjectNote = (id: string, data: UpdateSubjectNoteRequest) =>
  api.patch(url.UPDATE_SUBJECT_NOTE_URL(id), data);

export const deleteSubjectNote = (id: string) =>
  api.delete(url.DELETE_SUBJECT_NOTE_URL(id));

export const uploadSubjectNote = (file: File, id?: string | null) =>
  api.uploadFile(url.UPLOAD_SUBJECT_NOTE_URL(id), file);

export const downloadSubjectNote = (id: string) =>
  api.downloadFile(url.DOWNLOAD_SUBJECT_NOTE_URL(id));

export const toggleSubjectNote = (noteId: string) =>
  api.patch(url.TOGGLE_SUBJECT_NOTE_URL(noteId), {});

//#endregion

//#region Chapters

export const getChapters = (data: PaginateQuery) =>
  api.get(url.GET_CHAPTERS_URL(data));

export const getChapter = (id: string) => api.get(url.GET_CHAPTER_URL(id));

export const getChaptersStats = (subjectId?: string) =>
  api.get(url.GET_CHAPTERS_STATS_URL(subjectId));

export const addChapter = (data: CreateChapterRequest) =>
  api.post(url.ADD_CHAPTER_URL(), data);

export const updateChapter = (id: string, data: UpdateChapterRequest) =>
  api.patch(url.UPDATE_CHAPTER_URL(id), data);

export const deleteChapter = (id: string) =>
  api.delete(url.DELETE_CHAPTER_URL(id));

export const getChaptersSkillsStats = (subjectId?: string) =>
  api.get(url.GET_CHAPTERS_SKILLS_STATS_URL(subjectId));

export const getChaptersFlashCardsStats = (subjectId?: string) =>
  api.get(url.GET_CHAPTERS_FLASH_CARDS_STATS_URL(subjectId));

export const getChaptersLecturesStats = (subjectId?: string) =>
  api.get(url.GET_CHAPTERS_LECTURES_STATS_URL(subjectId));

export const toggleChapterState = (id: string) =>
  api.patch(url.TOGGLE_CHAPTER_STATE_URL(id), {});

export const getChaptersSelectListBySubject = (subjectId: string) =>
  api.post(url.GET_CHAPTER_SELECT_LIST_BY_SUBJECT_URL(subjectId), {});

export const getOrganizingChapters = (subjectId: string, data: PaginateQuery) =>
  api.get(url.GET_ORGANIZING_CHAPTERS_URL(subjectId, data));

//#endregion

//#region Lessons

export const getLessons = (data: PaginateQuery) =>
  api.get(url.GET_LESSONS_URL(data));

export const getLesson = (id: string) => api.get(url.GET_LESSON_URL(id));

export const getLessonDetail = (id: string) =>
  api.get(url.GET_LESSON_DETAIL_URL(id));

export const addLesson = (data: CreateLessonRequest) =>
  api.post(url.ADD_LESSON_URL(), data);

export const updateLesson = (id: string, data: UpdateLessonRequest) =>
  api.patch(url.UPDATE_LESSON_URL(id), data);

export const deleteLesson = (id: string) =>
  api.delete(url.DELETE_LESSON_URL(id));

export const toggleLessonState = (id: string) =>
  api.patch(url.TOGGLE_LESSON_STATE_URL(id), {});

export const getLessonSelectListByChapter = (chapterId: string) =>
  api.get(url.GET_LESSON_SELECT_LIST_BY_CHAPTER_URL(chapterId));

//#endregion

//#region Question

export const getQuestion = (id: string) => api.get(url.GET_QUESTION_URL(id));

export const getQuestionsByLesson = (
  lessonId: string,
  model?: QuestionModelEnum
) => api.get(url.GET_QUESTIONS_BY_LESSON_URL(lessonId, model));

export const addQuestion = (data: CreateQuestionRequest) =>
  api.post(url.ADD_QUESTION_URL(), data);

export const updateQuestion = (id: string, data: UpdateQuestionRequest) =>
  api.patch(url.UPDATE_QUESTION_URL(id), data);

export const deleteQuestion = (id: string) =>
  api.delete(url.DELETE_QUESTION_URL(id));

export const uploadQuestionDisplayImage = (file: File, questionId?: string) =>
  api.uploadFile(url.UPLOAD_QUESTION_DISPLAY_IMAGE_URL(questionId), file);

export const toggleQuestionState = (id: string) =>
  api.patch(url.TOGGLE_QUESTION_STATE_URL(id), {});

export const uploadQuestionImage = (file: File, tempId: string) =>
  api.uploadFile(url.UPLOAD_QUESTION_IMAGE_URL(tempId), file);

//#endregion

//#region Course Durations

export const getCourseDurations = (
  gradeId: string,
  courseId: string,
  data: PaginateQuery
) => api.get(url.GET_COURSE_DURATIONS_URL(gradeId, courseId, data));

export const getCourseDuration = (id: string) =>
  api.get(url.GET_COURSE_DURATION_URL(id));

export const updateCourseDuration = (data: UpdateCourseDurationRequest[]) =>
  api.patch(url.UPDATE_COURSE_DURATION_URL(), data);

export const deleteCourseDuration = (id: string) =>
  api.delete(url.DELETE_COURSE_DURATION_URL(id));

export const rollover = () => api.patch(url.ROLLOVER_URL(), null);

//#endregion

//#region Skills

export const getSkills = (data: PaginateQuery) =>
  api.get(url.GET_SKILLS_URL(data));

export const getSkillsByLesson = (lessonId: string) =>
  api.get(url.GET_SKILLS_BY_LESSON_URL(lessonId));

export const getSkillsBySubject = (subjectId: string) =>
  api.get(url.GET_SKILLS_BY_SUBJECT_URL(subjectId));

export const getSkill = (id: string) => api.get(url.GET_SKILL_URL(id));

export const addSkill = (data: CreateSkillRequest) =>
  api.post(url.ADD_SKILL_URL(), data);

export const updateSkill = (id: string, data: UpdateSkillRequest) =>
  api.patch(url.UPDATE_SKILL_URL(id), data);

export const deleteSkill = (id: string) => api.delete(url.DELETE_SKILL_URL(id));

export const uploadSkillVideo = (file: File, skillId?: string) =>
  api.uploadSkillVideoFile(url.UPLOAD_SKILL_VIDEO_URL(skillId), file);

export const getSkillsSuggestion = (request: SkillsSuggestionRequest) =>
  api.post(url.GET_SKILLS_SUGGESTION_URL(), request);

export const toggleSkillState = (id: string) =>
  api.patch(url.TOGGLE_SKILL_STATE_URL(id), {});

export const copySkills = (request: CopyCutSkillRequest) =>
  api.post(url.COPY_SKILLS_URL, request);

//#endregion

//#region Flash Cards

export const getFlashCards = (data: PaginateQuery) =>
  api.get(url.GET_FLASH_CARDS_URL(data));

export const getFlashCardsByLesson = (lessonId: string) =>
  api.get(url.GET_FLASH_CARDS_BY_LESSON_URL(lessonId));

export const getFlashCard = (id: string) => api.get(url.GET_FLASH_CARD_URL(id));

export const addFlashCard = (data: CreateFlashCardRequest) =>
  api.post(url.ADD_FLASH_CARD_URL(), data);

export const updateFlashCard = (id: string, data: UpdateFlashCardRequest) =>
  api.patch(url.UPDATE_FLASH_CARD_URL(id), data);

export const deleteFlashCard = (id: string) =>
  api.delete(url.DELETE_FLASH_CARD_URL(id));

export const uploadFlashCardImage = (file: File, flashCardId?: string) =>
  api.uploadFile(url.UPLOAD_FLASH_CARD_IMAGE_URL(flashCardId), file);

export const deleteAllFlashCardByLessonId = (lessonId: string) =>
  api.delete(url.DELETE_ALL_FLASH_CARD_BY_LESSON_ID_URL(lessonId));

export const toggleFlashCardState = (id: string) =>
  api.patch(url.TOGGLE_FLASH_CARD_STATE_URL(id), {});

export const uploadAndCreateFlashCard = (
  request: UploadAndCreateFlashCardRequest
) =>
  api.uploadFlashCardFile(
    url.UPLOAD_AND_CREATE_FLASH_CARD_URL(request),
    request.file,
    request.localId
  );

//#endregion

//#region Calendar

export const getCalendarEvents = (data: PaginateQuery) =>
  api.get(url.GET_CALENDAR_EVENTS_URL(data));

export const getAllCalendarEvents = () =>
  api.get(url.GET_ALL_CALENDAR_EVENTS_URL());

export const getCalendarEvent = (id: string) =>
  api.get(url.GET_CALENDAR_EVENT_URL(id));

export const addCalendarEvent = (data: CreateCalendarEventRequest) =>
  api.post(url.ADD_CALENDAR_EVENT_URL(), data);

export const updateCalendarEvent = (
  id: string,
  data: UpdateCalendarEventRequest
) => api.patch(url.UPDATE_CALENDAR_EVENT_URL(id), data);

export const deleteCalendarEvent = (id: string) =>
  api.delete(url.DELETE_CALENDAR_EVENT_URL(id));

//#endregion

//#region App Settings

export const getAppSettings = (type: AppSettingsTypeEnum) =>
  api.get(url.GET_APP_SETTINGS_URL(type));

export const updateAppSettings = (
  type: AppSettingsTypeEnum,
  data: UpdateAppSettingsRequest
) => api.put(url.UPDATE_APP_SETTINGS_URL(type), data);

export const generateSequenceRequest = (
  request: GenerateSequenceNumberRequest
) => api.post(url.GENERATE_SEQUENCE_NUMBER_REQUEST, request);

//#endregion

//#region Supplication

export const getSupplications = (data: PaginateQuery) =>
  api.get(url.GET_SUPPLICATIONS_URL(data));

export const getSupplication = (id: string) =>
  api.get(url.GET_SUPPLICATION_URL(id));

export const addSupplication = (data: CreateSupplicationRequest) =>
  api.post(url.ADD_SUPPLICATION_URL(), data);

export const updateSupplication = (
  id: string,
  data: UpdateSupplicationRequest
) => api.patch(url.UPDATE_SUPPLICATION_URL(id), data);

export const deleteSupplication = (id: string) =>
  api.delete(url.DELETE_SUPPLICATION_URL(id));

//#endregion

//#region Banner

export const getBanners = (data: PaginateQuery) =>
  api.get(url.GET_BANNERS_URL(data));

export const getBanner = (id: string) => api.get(url.GET_BANNER_URL(id));

export const addBanner = (data: CreateBannerRequest) =>
  api.post(url.ADD_BANNER_URL(), data);

export const updateBanner = (id: string, data: UpdateBannerRequest) =>
  api.patch(url.UPDATE_BANNER_URL(id), data);

export const deleteBanner = (id: string) =>
  api.delete(url.DELETE_BANNER_URL(id));

export const uploadBannerImage = (file: File, bannerId?: string) =>
  api.uploadFile(url.UPLOAD_BANNER_IMAGE_URL(bannerId), file);

//#endregion

//#region Account

export const getUserProfile = () => api.get(url.GET_USER_PROFILE_URL());

export const updateUserProfile = (data: UpdateUserProfileRequest) =>
  api.patch(url.UPDATE_USER_PROFILE_URL(), data);

export const uploadUserProfilePhoto = (file: File) =>
  api.uploadFile(url.UPLOAD_USER_PROFILE_PHOTO_URL(), file);

export const changePassword = (request: ChangePasswordRequest) =>
  api.patch(url.CHANGE_PASSWORD_URL(), request);

//#endregion

//#region Teacher Admin Users

export const getTeacherAdminUsers = (role: RoleEnum, data: PaginateQuery) =>
  api.get(url.GET_TEACHER_ADMIN_USERS_URL(role, data));

export const getTeacherAdminUser = (id: string) =>
  api.get(url.GET_TEACHER_ADMIN_USER_URL(id));

export const addTeacherAdminUser = (data: CreateTeacherAdminRequest) =>
  api.post(url.ADD_TEACHER_ADMIN_USER_URL(), data);

export const updateTeacherAdminUser = (
  id: string,
  data: CreateTeacherAdminRequest
) => api.patch(url.UPDATE_TEACHER_ADMIN_USER_URL(id), data);

export const deleteTeacherAdminUser = (id: string) =>
  api.delete(url.DELETE_TEACHER_ADMIN_USER_URL(id));

export const toggleBlockUser = (id: string, state: boolean) =>
  api.patch(url.TOGGLE_BLOCK_USER(id, state), null);

//#endregion

//#region Olympic Question

export const getOlympicQuestion = (id: string) =>
  api.get(url.GET_OLYMPIC_QUESTION_URL(id));

export const getQuestionsByOlympic = (query: PaginateQuery) =>
  api.get(url.GET_QUESTIONS_BY_OLYMPIC_URL(query));

export const addOlympicQuestion = (data: CreateOlympicQuestionRequest) =>
  api.post(url.ADD_OLYMPIC_QUESTION_URL(), data);

export const updateOlympicQuestion = (
  id: string,
  data: UpdateOlympicQuestionRequest
) => api.patch(url.UPDATE_OLYMPIC_QUESTION_URL(id), data);

export const deleteOlympicQuestion = (id: string) =>
  api.delete(url.DELETE_OLYMPIC_QUESTION_URL(id));

export const uploadOlympicQuestionDisplayImage = (
  file: File,
  questionId?: string
) =>
  api.uploadFile(
    url.UPLOAD_OLYMPIC_QUESTION_DISPLAY_IMAGE_URL(questionId),
    file
  );

export const addUpdateOlympic = (data: CreateUpdateOlympicRequest) =>
  api.put(url.ADD_UPDATE_OLYMPIC_URL(), data);

export const getOlympic = (id: string) => api.get(url.GET_OLYMPIC_URL(id));

export const getOlympics = (query: PaginateQuery) =>
  api.get(url.GET_OLYMPICS(query));

export const deleteOlympic = (id: string) =>
  api.delete(url.DELETE_OLYMPIC_URL(id));

export const getOlympicParticipants = (data: PaginateQuery) =>
  api.get(url.GET_OLYMPIC_PARTICIPANTS(data));

//#endregion

//#region Promo Codes

export const getPromoCodes = (data: PaginateQuery) =>
  api.get(url.GET_PROMO_CODES_URL(data));

export const getPromoCode = (id: string) => api.get(url.GET_PROMO_CODE_URL(id));

export const addPromoCode = (data: CreatePromoCodeRequest) =>
  api.post(url.ADD_PROMO_CODE_URL(), data);

export const updatePromoCode = (id: string, data: UpdatePromoCodeRequest) =>
  api.patch(url.UPDATE_PROMO_CODE_URL(id), data);

export const deletePromoCode = (id: string) =>
  api.delete(url.DELETE_PROMO_CODE_URL(id));

//#endregion

//#region App Settings

export const getPlan = (id: string) => api.get(url.GET_PLAN(id));

export const addPlan = (data: UpdatePlanRequest) =>
  api.post(url.ADD_PLAN(), data);

export const updatePlan = (id: string, data: CreatePlanRequest) =>
  api.patch(url.UPDATE_PLAN(id), data);

export const getPlans = (courseId: string, gradeId: string) =>
  api.get(url.GET_PLANS(courseId, gradeId));

export const uploadPlanImage = (
  file: File,
  localId: number,
  callback: (progress: number, localId: number) => void,
  planId?: string
) =>
  api.uploadFileWithProgress(
    url.UPLOAD_PLAN_IMAGE_URL(planId),
    file,
    callback,
    localId
  );

export const getPurchaseablePlansForStudent = (
  courseId: string,
  gradeId: string,
  userId: string
) => api.get(url.GET_PURCHASEABLE_PLANS_FOR_STUDENT(courseId, gradeId, userId));

export const deletePlan = (id: string) => api.delete(url.DELETE_PLAN_URL(id));

export const togglePlanState = (id: string) =>
  api.patch(url.TOGGLE_PLAN_STATE_URL(id), {});

//#endregion

//#region students

export const getStudents = (data: PaginateQuery) =>
  api.get(url.GET_STUDENTS_URL(data));

export const getStudentsV2 = (data: PaginateQuery) =>
  api.get(url.GET_STUDENTS_V2_URL(data));
//#endregion

//#region parents

export const getParents = (data: PaginateQuery) =>
  api.get(url.GET_PARENTS_URL(data));
//#endregion

//#region teachers

export const getTeachers = (data: PaginateQuery) =>
  api.get(url.GET_TEACHERS_URL(data));
//#endregion

//#region dashboard
export const getAdminAnalytics = (data: AdminAnalyticsRequest) =>
  api.post(url.GET_ADMIN_ANALYTICS(), data);

export const getAdminAnalyticsV2 = (data: AdminAnalyticsRequest) =>
  api.post(url.GET_ADMIN_ANALYTICS_V2(), data);
//#endregion

//#region notifications
export const sendNotification = (data: SendNotificationRequest) =>
  api.post(url.SEND_NOTIFICATION(), data);

export const sendNotificationV3 = (data: SendNotificationV3Request) =>
  api.post(url.SEND_NOTIFICATION_V3(), data);

export const getAdminSentNotifications = (data: PaginateQuery) =>
  api.get(url.GET_ADMIN_SENT_NOTIFICATIONS(data));

export const deleteNotification = (id: string) =>
  api.delete(url.DELETE_NOTIFICATION_URL(id));

//#endregion

//#region Lectures

export const getLectures = (data: PaginateQuery) =>
  api.get(url.GET_LECTURES_URL(data));

export const getLecturesByLesson = (lessonId: string) =>
  api.get(url.GET_LECTURES_BY_LESSON_URL(lessonId));

export const getLecturesBySubject = (subjectId: string) =>
  api.get(url.GET_LECTURES_BY_SUBJECT_URL(subjectId));

export const getLecture = (id: string) => api.get(url.GET_LECTURE_URL(id));

export const addLecture = (data: CreateLectureRequest) =>
  api.post(url.ADD_LECTURE_URL(), data);

export const updateLecture = (id: string, data: UpdateLectureRequest) =>
  api.patch(url.UPDATE_LECTURE_URL(id), data);

export const deleteLecture = (id: string) =>
  api.delete(url.DELETE_LECTURE_URL(id));

export const uploadLectureVideo = (file: File, skillId?: string) =>
  api.uploadLectureVideoFile(url.UPLOAD_LECTURE_VIDEO_URL(skillId), file);

export const toggleLectureState = (id: string) =>
  api.patch(url.TOGGLE_LECTURE_STATE_URL(id), {});

//#endregion

//#region Complaints

export const getComplaints = (data: PaginateQuery) =>
  api.get(url.GET_COMPLAINTS_URL(data));

//#endregion

//#region transaction
export const getTransactionHistory = (data: PaginateQuery) =>
  api.get(url.GET_TRANSACTION_HISTORY_URL(data));

export const getTransactionHistoryV2 = (data: PaginateQuery) =>
  api.get(url.GET_TRANSACTION_HISTORY_V2_URL(data));
//#endregion

//#region purchase
export const manualPurchaseSubject = (data: ManualPurchaseRequest) =>
  api.post(url.MANUAL_PURCHASE_SUBJECT(), data);

//#endregion

//#region reports
export const getSubscriptionReport = (request: SubscriptionReportRequest) =>
  api.post(url.SUBSCRIPTION_REPORT(), request);

//#endregion

//#region App Configs

export const getAppConfigs = () => api.get(url.GET_APP_CONFIGS_URL());

export const updateAppConfigs = (request: UpdateAppConfigsRequest) =>
  api.patch(url.UPDATE_APP_CONFIGS_URL(), request);

//#endregion

//#region Homework Solution

export const getHomeworkSolutionQueries = () =>
  api.get(url.GET_HOMEWORK_SOLUTION_QUERIES());

export const updateHomeworkSolutionQueries = (
  request: HomeworkSolutionQueryDto
) => api.put(url.UPDATE_HOMEWORK_SOLUTION_QUERIES(), request);

//#endregion

//#region subscription
export const manualSubscription = (data: ManualPurchaseRequest) =>
  api.post(url.MANUAL_SUBSCRIPTION_SUBJECT(), data);

//#endregion

//#region Template
export const getTemplates = () => api.get(url.GET_TEMPLATES());
//#endregion
